<template>
    <div class="w-full">
        <template v-if="applications.length > 0">
            <div class="bg-gray-200 border-l-4 border-info-500 mb-6 p-4 rounded shadow w-full" role="alert">
                <p class="mb-0" v-html="changeSubscriptionPlanInfo"></p>
            </div>
            <div v-for="application in applications" class="mb-4">
                <label class="flex mb-2 text-sm">
                    <div class="flex items-center">
                        <template v-if="application.slug === 'crm'">
                            <div class="bg-purple-500 flex h-6 items-center justify-center mr-2 rounded-full w-6" style="padding: 0.35rem;">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="block fill-current h-6 text-white w-6">
                                    <path d="M8 24l3-9h-9l14-15-3 9h9l-14 15z"></path>
                                </svg>
                            </div>
                        </template>
                        <template v-else-if="application.slug === 'tasks'">
                            <div class="bg-red-500 flex h-6 items-center justify-center mr-2 rounded-full w-6" style="padding: 0.35rem;">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="block fill-current h-8 text-white w-8">
                                    <path d="M4.967 15c-2.743 0-4.967-2.238-4.967-5 0-2.761 2.224-5 4.967-5h5.033v10h-5.033zm7.04 7.475c-.52-.424-.902-.994-1.095-1.637l-1.151-3.827h-5.125l1.905 5.883c.214.659.828 1.106 1.521 1.106h3.439c.358 0 .677-.225.797-.562.12-.337.015-.713-.263-.939l-.028-.024zm-.007-17.475v10c3.164.509 7.161 1.992 12 5v-20c-4.964 3.085-8.876 4.502-12 5z"></path>
                                </svg>
                            </div>
                        </template>
                        <template v-else-if="application.slug === 'budgets'">
                            <div class="bg-blue-500 flex h-6 items-center justify-center mr-2 rounded-full w-6" style="padding: 0.35rem;">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="block fill-current h-8 text-white w-8">
                                    <path d="M5 19h-4v-4h4v4zm6 0h-4v-8h4v8zm6 0h-4v-13h4v13zm6 0h-4v-19h4v19zm1 2h-24v2h24v-2z"></path>
                                </svg>
                            </div>
                        </template>
                        <h2 class="font-thin text-lg mr-1 my-0">{{ application.name }}</h2>
                        <div class="font-normal self-end text-gray-body-light">{{ getCurrentResourcesText(application) }}</div>
                    </div>
                    <a v-if="dateIsInFuture(application.pivot.trial_ends_at)" class="font-medium inline-flex items-center label label-danger ml-2 uppercase"
                        href="javascript:;"
                        v-tooltip="{
                            content: $t('organization_payments.trial_subscription_info',
                            {
                                date: formatDate(application.pivot.trial_ends_at),
                                days: getDaysLeft(application.pivot.trial_ends_at),
                            }),
                            placement: 'right' }">
                        {{ $t('organization_payments.trial_subscription') }}
                        <span class="icon icon-question-circle icon-fw icon-lg ml-px"></span>
                    </a>
                </label>
                <div class="-m-4 py-2 flex flex-wrap relative">
                    <template v-if="getTotalScreens(application) > 1">
                        <button @click="previousScreen(application)" class="absolute bg-gray-body p-5px rounded-full top-1/2 z-50" :class="{ 'hover:bg-gray-body-light' : getCurrentScreen(application) > 1 }" v-tooltip="{ content: $t('globals.previous'), placement: 'bottom' }">
                            <div class="h-5 text-white w-5" :class="{ 'text-opacity-50' : getCurrentScreen(application) === 1 }">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd" />
                                </svg>
                            </div>
                        </button>
                        <button @click="nextScreen(application)" class="absolute bg-gray-body p-5px right-0 rounded-full top-1/2 z-50" :class="{ 'hover:bg-gray-body-light' : getCurrentScreen(application) < getTotalScreens(application) }" v-tooltip="{ content: $t('globals.next'), placement: 'bottom' }">
                            <div class="h-5 text-white w-5" :class="{ 'text-opacity-50' : getCurrentScreen(application) === getTotalScreens(application) }">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                    <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
                                </svg>
                            </div>
                        </button>
                    </template>
                    <template v-for="(subscriptionPlan, index) in application.subscription_plans">
                        <div v-if="index >= (getCurrentScreen(application) - 1) * 4 && index <= getCurrentScreen(application) * 4 - 1" class="p-4 md:w-1/2 lg:w-1/4 w-full">
                            <subscription-plan-card
                                :application="application"
                                :subscription-plan="subscriptionPlan"
                                :is-clickable="true">
                            </subscription-plan-card>
                        </div>
                    </template>
                </div>
            </div>
        </template>
        <div v-else class="flex flex-col items-center justify-center w-full">
            <div class="md:w-1/4 pb-8 w-1/2">
                <svg-select-option></svg-select-option>
            </div>
            <div class="md:w-3/4 text-center w-full">
                <h5>{{ $t('organization_applications.no_applications') }}</h5>
                <p v-html="$t('organization_payments.no_applications_installed', { linkOpen: '<a href=&quot;/dashboard&quot;>', linkClose: '</a>' })"></p>
            </div>
        </div>
    </div>
</template>

<script>
import { differenceInCalendarDays, lightFormat, isAfter, parseISO } from 'date-fns';
import SubscriptionPlanCard from './subscription-plan-card.vue';
import SvgSelectOption from '../../../svg/select-option.vue';

export default {
    components: {
        'subscription-plan-card': SubscriptionPlanCard,
        'svg-select-option': SvgSelectOption,
    },

    props: {
        applications: {
            type: Array,
            required: true,
        },
    },

    data() {
        return {
            subscriptionPlanScreens: this.applications.map(application => {
                return {
                    application_id: application.id,
                    screen: this.getInitialScreen(application),
                };
            }),
        };
    },

    computed: {
        changeSubscriptionPlanInfo() {
            return `${this.$t('organization_payments.change_subscription_plan')}
                <ul class="list-disc list-inside mb-0 mt-2">
                    <li>${this.$t('organization_payments.upgrading_plan_info')}</li>
                    <li>${this.$t('organization_payments.downgrading_plan_info')}</li>
                </ul>`;
        },
    },

    methods: {
        getCurrentResourcesText(application) {
            let currentResources = [];
            if (application.pivot) {
                currentResources.push(this.$tc('organizations.users_count', application.pivot.user_accesses.length, { value: application.pivot.user_accesses.length }));
            } else {
                currentResources.push(this.$tc('organizations.users_count', 0, { value: 0 }));
            }
            if (application.slug === 'budgets') {
                currentResources.push(this.$tc('organization_applications.subdivisions_count', application.pivot.main_entities_count, { value: application.pivot.main_entities_count }));
            }

            return `(${currentResources.join(', ')})`;
        },

        getCurrentScreen(application) {
            return this.subscriptionPlanScreens
                .find(item => item.application_id === application.id)
                .screen;
        },

        getTotalScreens(application) {
            return Math.ceil(application.subscription_plans.length / 4);
        },

        previousScreen(application) {
            if (this.getCurrentScreen(application) > 1) {
                const key = this.subscriptionPlanScreens.findIndex(item => item.application_id === application.id);
                this.subscriptionPlanScreens[key].screen = this.subscriptionPlanScreens[key].screen - 1;
            }
        },

        nextScreen(application) {
            if (this.getCurrentScreen(application) < this.getTotalScreens(application)) {
                const key = this.subscriptionPlanScreens.findIndex(item => item.application_id === application.id);
                this.subscriptionPlanScreens[key].screen = this.subscriptionPlanScreens[key].screen + 1;
            }
        },

        getInitialScreen(application) {
            if (! application.pivot) {
                return 1;
            }
            if (! application.pivot.subscription_plan) {
                return 1;
            }
            const currentPlanIndex = application.subscription_plans.findIndex(subscriptionPlan => subscriptionPlan.id === application.pivot.subscription_plan.id);
            return Math.ceil((currentPlanIndex + 1) / 4);
        },

        formatDate(date) {
            return lightFormat(parseISO(date), 'dd-MM-y');
        },

        getDaysLeft(date) {
            return differenceInCalendarDays(parseISO(date), parseISO(window.Globals.now));
        },

        dateIsInFuture(date) {
            return isAfter(parseISO(date), parseISO(window.Globals.now));
        },
    },
};
</script>
